import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './WorkspaceToolbar.css';
import { useAuth } from '../context/AuthContext';

const API = window.__CRM_API__ || 'http://localhost:8000/api';

export default function WorkspaceToolbar({ onNavigate }) {
  const { usuario, loading: authLoading } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const timer = useRef();
  const previousUnread = useRef(null);
  const audioContext = useRef(null);

  const unlockNotificationSound = () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      if (!audioContext.current) audioContext.current = new AudioContextClass();
      if (audioContext.current.state === 'suspended') audioContext.current.resume();
      localStorage.setItem('qlf-notification-sound', 'enabled');
    } catch { /* El navegador volverá a intentarlo en el siguiente gesto. */ }
  };

  const playNotificationSound = () => {
    // Web Audio evita depender de archivos externos. El navegador solo lo
    // reproducirá después de una interacción válida, como corresponde.
    try {
      unlockNotificationSound();
      const context = audioContext.current;
      if (!context || context.state !== 'running') return;
      [0, 0.13].forEach((delay, index) => {
        const oscillator = context.createOscillator(); const gain = context.createGain();
        oscillator.type = 'sine'; oscillator.frequency.value = index ? 1047 : 784;
        gain.gain.setValueAtTime(0.0001, context.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.09, context.currentTime + delay + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + delay + 0.11);
        oscillator.connect(gain); gain.connect(context.destination);
        oscillator.start(context.currentTime + delay); oscillator.stop(context.currentTime + delay + 0.12);
      });
    } catch { /* El sonido nunca debe impedir que aparezca la alerta. */ }
  };

  const loadNotifications = async () => { try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const payload = (await axios.get(`${API}/workspace/notifications`, { headers: { Authorization: `Bearer ${token}` } })).data;
    // A malformed or legacy API payload must not crash the global header and
    // make the complete workspace look unavailable.
    const next = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []);
    const nextUnread = next.filter((item) => !item.read).length;
    if (previousUnread.current !== null && nextUnread > previousUnread.current) playNotificationSound();
    previousUnread.current = nextUnread;
    setNotifications(next);
    // Sidebar badges consume the same payload.  Publishing it prevents a
    // second request to the API every time notifications refresh.
    window.dispatchEvent(new CustomEvent('qlf-notifications-updated', { detail: { items: next } }));
  } catch { setNotifications([]); } };
  // El primer clic/tap habilita el AudioContext. Es una regla de seguridad de
  // Chrome, Safari y navegadores móviles, no una restricción del CRM.
  useEffect(() => {
    const enable = () => unlockNotificationSound();
    window.addEventListener('pointerdown', enable, { once: true, capture: true });
    return () => window.removeEventListener('pointerdown', enable, { capture: true });
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (authLoading || !usuario) return undefined; loadNotifications(); const interval = window.setInterval(loadNotifications, 60000); return () => window.clearInterval(interval); }, [authLoading, usuario]);
  useEffect(() => { clearTimeout(timer.current); if (query.trim().length < 2) { setResults([]); return; } timer.current = setTimeout(async () => { try { setResults((await axios.get(`${API}/workspace/search`, { params: { q: query } })).data); setSearchOpen(true); } catch { setResults([]); } }, 220); return () => clearTimeout(timer.current); }, [query]);
  const navigate = (entry) => { onNavigate(entry); setQuery(''); setResults([]); setSearchOpen(false); };
  const markRead = async (keys, all = false) => { try {
    await axios.post(`${API}/workspace/notifications/read`, { keys, all });
    await loadNotifications();
    // Sidebar and module dots refresh immediately after a user reads an
    // alert; polling remains as a fallback for notifications from other tabs.
    window.dispatchEvent(new Event('qlf-notifications-changed'));
  } catch {} };
  const unread = notifications.filter((item) => !item.read).length;

  return <div className="workspace-tools">
    <div className="global-search"><span>🔎</span><input value={query} onFocus={() => query && setSearchOpen(true)} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar clientes, A-Number, casos, jueces o documentos…" aria-label="Búsqueda global" />
      {searchOpen && <div className="tool-popover search-results">{results.length ? results.map((result) => <button key={result.key} onClick={() => navigate(result)}><span>{result.type}</span><b>{result.title}</b><small>{result.subtitle}</small></button>) : <p>{query.length >= 2 ? 'No se encontraron registros.' : 'Escriba al menos dos caracteres.'}</p>}</div>}
    </div>
    <div className="notification-wrap"><button className="notification-button" onClick={() => setNotificationOpen(!notificationOpen)} aria-label="Centro de notificaciones">🔔{unread > 0 && <b>{unread > 9 ? '9+' : unread}</b>}</button>
      {notificationOpen && <div className="tool-popover notification-popover"><header><div><strong>Notificaciones</strong><small>{unread} sin leer</small></div><button onClick={() => markRead([], true)}>Marcar todas como leídas</button></header>{notifications.length ? notifications.map((item) => <button className={`notification-item ${item.read ? 'read' : ''}`} key={item.key} onClick={() => { markRead([item.key]); onNavigate(item.section); setNotificationOpen(false); }}><span>{item.icon}</span><div><b>{item.title}</b><small>{item.body}</small></div></button>) : <p className="tools-empty">✅ No hay alertas pendientes.</p>}</div>}
    </div>
  </div>;
}
